const APP_SHELL_SW_URL = "/app-shell-sw.js";
const APP_MANIFEST_URL = "/asset-manifest.json";
const APP_MANIFEST_SIGNATURE_KEY = "fresh_app_manifest_signature_v1";
const SERVICE_WORKER_READY_TIMEOUT_MS = 3500;
const SERVICE_WORKER_MESSAGE_TIMEOUT_MS = 8000;

const isIOSWebKit = () => {
  if (typeof navigator === "undefined") {
    return false;
  }

  return /iP(ad|hone|od)/.test(navigator.userAgent)
    || (navigator.userAgent.includes("Macintosh") && "ontouchend" in window);
};

type WindowWithBootFallback = Window & {
  __freshClearCacheAndReload?: () => void;
};

const setPreloaderMessage = (title: string, text: string) => {
  const titleNode = document.getElementById("app-preloader-title");
  const textNode = document.getElementById("app-preloader-text");

  if (titleNode) {
    titleNode.textContent = title;
  }

  if (textNode) {
    textNode.textContent = text;
  }
};

const setPreloaderFile = (fileLabel: string) => {
  const fileNode = document.getElementById("app-preloader-file");

  if (fileNode) {
    fileNode.textContent = fileLabel;
  }
};

const setPreloaderRetryVisible = (visible: boolean) => {
  const retryNode = document.getElementById("app-preloader-retry");

  if (!retryNode) {
    return;
  }

  retryNode.classList.toggle("is-visible", visible);
};

const attachPreloaderRetryHandler = () => {
  const retryNode = document.getElementById("app-preloader-retry");

  if (!retryNode) {
    return;
  }

  retryNode.onclick = () => {
    (window as WindowWithBootFallback).__freshClearCacheAndReload?.();
  };
};

const normalizeUrl = (url: string) => {
  if (!url) {
    return null;
  }

  if (url.startsWith("http://") || url.startsWith("https://")) {
    try {
      const parsedUrl = new URL(url);
      return `${parsedUrl.pathname}${parsedUrl.search}`;
    } catch {
      return url;
    }
  }

  return url.startsWith("/") ? url : `/${url}`;
};

const buildManifestSignature = (manifest: Record<string, unknown>) =>
  JSON.stringify({
    files: manifest.files ?? null,
    entrypoints: manifest.entrypoints ?? null,
  });

const buildCacheVersion = (signature: string) => {
  let hash = 5381;

  for (let index = 0; index < signature.length; index += 1) {
    hash = (hash * 33) ^ signature.charCodeAt(index);
  }

  return `v${(hash >>> 0).toString(36)}`;
};

const extractManifestUrls = (manifest: Record<string, unknown>) => {
  const urls = new Set<string>(["/", "/index.html"]);
  const manifestFiles = manifest.files && typeof manifest.files === "object"
    ? Object.values(manifest.files as Record<string, unknown>)
    : [];

  manifestFiles.forEach((value) => {
    if (typeof value === "string") {
      const normalizedUrl = normalizeUrl(value);

      if (normalizedUrl) {
        urls.add(normalizedUrl);
      }
    }
  });

  urls.add(APP_MANIFEST_URL);

  return Array.from(urls);
};

const formatProgressFile = (url: string) => {
  const normalizedUrl = normalizeUrl(url) || url;

  if (normalizedUrl === "/" || normalizedUrl === "/index.html") {
    return "Обновляем оболочку приложения";
  }

  return `Загружаем ${normalizedUrl}`;
};

const sendMessageToServiceWorker = async (
  message: Record<string, unknown>,
  options?: {
    onProgress?: (payload: { url: string; index: number; total: number }) => void;
    timeoutMs?: number;
  }
) => {
  if (!("serviceWorker" in navigator)) {
    return false;
  }

  const registration = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => {
      window.setTimeout(() => resolve(null), SERVICE_WORKER_READY_TIMEOUT_MS);
    }),
  ]);

  if (!registration) {
    return false;
  }

  const activeWorker = registration.active ?? navigator.serviceWorker.controller;

  if (!activeWorker) {
    return false;
  }

  return new Promise<boolean>((resolve) => {
    const channel = new MessageChannel();
    const timeoutId = window.setTimeout(() => {
      channel.port1.onmessage = null;
      channel.port1.close();
      resolve(false);
    }, options?.timeoutMs ?? SERVICE_WORKER_MESSAGE_TIMEOUT_MS);

    const finish = (success: boolean) => {
      window.clearTimeout(timeoutId);
      channel.port1.onmessage = null;
      channel.port1.close();
      resolve(success);
    };

    channel.port1.onmessage = (event) => {
      if (event.data?.type === "progress") {
        options?.onProgress?.(event.data);
        return;
      }

      finish(Boolean(event.data?.success));
    };

    activeWorker.postMessage(message, [channel.port2]);
  });
};

const fetchLatestManifest = async () => {
  const response = await fetch(`${APP_MANIFEST_URL}?ts=${Date.now()}`, {
    cache: "no-store",
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Не удалось получить актуальный asset-manifest");
  }

  return response.json() as Promise<Record<string, unknown>>;
};

export const registerAppShellServiceWorker = async () => {
  if (!("serviceWorker" in navigator)) {
    setPreloaderFile("Service Worker не поддерживается, продолжаем без него");
    return null;
  }

  try {
    setPreloaderFile("Регистрируем Service Worker");
    const registration = await navigator.serviceWorker.register(APP_SHELL_SW_URL, {
      updateViaCache: "none",
    });
    setPreloaderFile("Проверяем обновление Service Worker");
    await registration.update().catch(() => undefined);

    return registration;
  } catch {
    setPreloaderFile("Service Worker не зарегистрировался, продолжаем без кэша");
    return null;
  }
};

export const prepareCachedAppShell = async () => {
  if (!("serviceWorker" in navigator)) {
    setPreloaderFile("Service Worker не поддерживается, кэширование пропущено");
    return;
  }

  const registration = await registerAppShellServiceWorker();

  if (!registration) {
    return;
  }

  if (isIOSWebKit()) {
    setPreloaderFile("Safari iOS: кэш приложения пропущен");
    return;
  }

  try {
    setPreloaderFile("Запрашиваем asset-manifest.json");
    const manifest = await fetchLatestManifest();
    setPreloaderFile("Разбираем список файлов приложения");
    const signature = buildManifestSignature(manifest);
    const cacheVersion = buildCacheVersion(signature);
    const previousSignature = window.localStorage.getItem(APP_MANIFEST_SIGNATURE_KEY);
    const urls = extractManifestUrls(manifest);
    let updateTimeoutId: number | null = null;

    if (previousSignature && previousSignature !== signature) {
      setPreloaderMessage("Обновление", "Пожалуйста, подождите, получаем актуальные файлы.");
      setPreloaderFile("");
      setPreloaderRetryVisible(false);
      attachPreloaderRetryHandler();

      updateTimeoutId = window.setTimeout(() => {
        setPreloaderRetryVisible(true);
      }, 5000);

      const cached = await sendMessageToServiceWorker({
        type: "PRECACHE_URLS",
        version: cacheVersion,
        urls,
      }, {
        timeoutMs: 12000,
        onProgress: ({ url, index, total }) => {
          setPreloaderMessage("Обновление", `Пожалуйста, подождите, получаем актуальные файлы (${index}/${total}).`);
          setPreloaderFile(formatProgressFile(url));
        },
      });

      if (updateTimeoutId) {
        window.clearTimeout(updateTimeoutId);
      }

      if (cached) {
        window.localStorage.setItem(APP_MANIFEST_SIGNATURE_KEY, signature);
        window.location.reload();
        return;
      }
    }

    window.localStorage.setItem(APP_MANIFEST_SIGNATURE_KEY, signature);
    setPreloaderFile("Фоново обновляем кэш приложения");
    void sendMessageToServiceWorker({
      type: "PRECACHE_URLS",
      version: cacheVersion,
      urls,
    });
  } catch {
    setPreloaderFile("Не удалось обновить кэш приложения, продолжаем загрузку");
    // ignore manifest/cache issues and continue boot
  }
};
